import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

const LanguageContext = createContext(null);

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'rw', name: 'Kinyarwanda' }
];

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferred_language') || 'en';
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Sync language with backend when authenticated. If the backend has a
  // recorded preference, adopt it. If it doesn't (user never changed the
  // locale), persist the current frontend choice so marketing filters and
  // future devices can rely on `users.preferences.language` being set.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/user/preferences`, { withCredentials: true });
        const backendLang = res.data?.language;
        if (backendLang) {
          setLanguage(backendLang);
          localStorage.setItem('preferred_language', backendLang);
        } else {
          // No recorded preference — push the current frontend value once.
          const current = localStorage.getItem('preferred_language') || 'en';
          try {
            await axios.put(`${API}/user/preferences`, { language: current }, { withCredentials: true });
          } catch (e) { /* non-fatal */ }
        }
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const changeLanguage = async (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('preferred_language', newLang);
    
    // Save to backend if authenticated
    if (isAuthenticated) {
      try {
        await axios.put(`${API}/user/preferences`, { language: newLang }, { withCredentials: true });
      } catch (error) {
        console.error('Failed to save language preference');
      }
    }
  };

  // Helper function to get translated content
  const getTranslatedContent = (item, field) => {
    if (!item) return '';
    
    // If current language is English or no translations exist, return original
    if (language === 'en' || !item.translations || !item.translations[language]) {
      return item[field] || '';
    }
    
    // Return translated content or fall back to original
    return item.translations[language]?.[field] || item[field] || '';
  };

  // Check if content is available in current language
  const isLanguageAvailable = (availableLanguages) => {
    if (!availableLanguages || availableLanguages.length === 0) return true;
    return availableLanguages.includes(language);
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  };

  return (
    <LanguageContext.Provider value={{
      language,
      changeLanguage,
      getTranslatedContent,
      isLanguageAvailable,
      getCurrentLanguageInfo,
      supportedLanguages: SUPPORTED_LANGUAGES,
      isAuthenticated
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}

export default LanguageContext;
